<template>
  <div>
    <div class="w-full">
      <div
        v-if="show_w8 == false"
        class="mb-5 flex flex-col items-start"
      >
        <template v-if="isW9Optional && !submitW9Anyway">
          <h4 class="mb-8 mt-4 text-left text-dark-gray sm:mt-2">
            {{
              brokerName + ' does not require carriers to complete Form W-9.'
            }}
          </h4>
        </template>
        <template v-else>
          <h4 class="mb-8 mt-4 text-left text-dark-gray sm:mt-2">
            {{
              firstTime
                ? 'Please provide a tax identification number and federal tax classification for your W-9 Form.'
                : 'Review your tax identification number and federal tax classification for your W-9 Form.'
            }}
          </h4>
          <div
            v-if="firstTime"
            class="w-full"
          >
            <div
              class="w-full border-b-2 border-light-gray text-sm text-body-color"
            >
              <div class="flex gap-2">
                <p class="pb-1">W-9 Information</p>
                <div
                  class="cursor-pointer text-yellow-500 hover:underline"
                  @click="onShowW8"
                >
                  Complete W-8 instead
                </div>
                <div class="-ml-1.5">
                  <tax-tool-tip-large
                    color-scheme="light"
                    :content="tax_form_selection_tooltip"
                    class="relative"
                    style="top: 1.5px"
                  >
                    <div class="tooltipStyle">
                      <div v-html="tax_form_selection_tooltip" />
                    </div>
                  </tax-tool-tip-large>
                </div>
              </div>
            </div>
            <div class="flex w-full gap-x-4">
              <div class="w-5/12 sm:w-1/4">
                <toggle-button
                  ref="einSsnToggleButton"
                  :items="tinOption"
                  :initial-value="
                    ['EIN', 'SSN'].includes(selectedTaxNumberOption?.label)
                      ? selectedTaxNumberOption?.label
                      : defaultTinOption
                  "
                  container-class="my-3"
                  :active-toggle-bg-yellow="true"
                  @selected-value="handleDropdownSelectTinType"
                />
              </div>
              <div class="flex w-full flex-row-reverse">
                <div
                  v-if="tinType === 'ein'"
                  class="relative my-3 flex w-full flex-col items-start"
                >
                  <input
                    :id="tinType"
                    v-model="taxNumber"
                    v-mask="'##-#######'"
                    class="input-padding w-full rounded border text-1md text-sm focus:text-dark-gray"
                    :class="[
                      validateIdentifier('ein', taxNumber) == false
                        ? 'failed-validation border-red-500 bg-red-100 text-red-500 focus:border-red-500 focus:bg-red-100 focus:text-red-500'
                        : 'border-transparent bg-off-white text-body-color focus:bg-white',
                    ]"
                    type="text"
                    placeholder="Enter EIN"
                    name="tin_value"
                  />
                  <span
                    v-if="validateIdentifier('ein', taxNumber) == false"
                    class="pl-1 pt-0.5 text-left text-2xs text-red-500"
                    >Invalid entry: {{ validationMessage?.ein }}</span
                  >
                </div>
                <div
                  v-else-if="tinType === 'ssn'"
                  class="my-3 flex w-full flex-col items-start"
                >
                  <input
                    :id="tinType"
                    v-model="taxNumber"
                    v-mask="'###-##-####'"
                    class="input-padding w-full rounded border text-1md text-sm focus:text-dark-gray"
                    :class="[
                      validateIdentifier('ssn', taxNumber) == false
                        ? 'failed-validation border-red-500 bg-red-100 text-red-500 focus:border-red-500 focus:bg-red-100 focus:text-red-500'
                        : 'border-transparent bg-off-white text-body-color focus:bg-white',
                    ]"
                    type="text"
                    placeholder="Enter SSN"
                    name="tin_value"
                  />
                  <span
                    v-if="validateIdentifier('ssn', taxNumber) == false"
                    class="pl-1 pt-0.5 text-left text-2xs text-red-500"
                    >Invalid entry: {{ validationMessage?.ssn }}</span
                  >
                </div>
              </div>
            </div>
            <div class="my-3 w-full flex-col">
              <p class="categoryParagraph">Federal Tax Classification</p>
              <div
                v-if="federalTaxClass?.length > 0"
                class="flex flex-col gap-2"
              >
                <round-radio-button
                  v-for="item in federalTaxClass"
                  :key="item"
                  :item="item.name"
                  :handle-click="
                    ($event) => handleSelectRadio($event, item.value)
                  "
                  name="federal_tax_class"
                  :value="item.name"
                  :checked="
                    data.federal_tax_class === item.value ||
                    checkLLC(data?.federal_tax_class, item?.value)
                  "
                />
              </div>
              <div v-if="selected">
                <dropdown-select
                  :options="selectOptions"
                  :default="
                    checkLLCSub(data?.federal_tax_class) ?? 'Enter C, S or P'
                  "
                  label="Tax Classification (Required)"
                  class="text-sm"
                  custom-class="categoryParagraph-override"
                  @input="handleDropdownSelect"
                />
                <span class="flex w-80 md:w-full">
                  <p class="pt-0 text-2xs text-body-color">
                    Note: Limited Liability Company Tax Classifications (C = C
                    Corporation, S = S Corporation, P = Partnership)
                  </p>
                </span>
              </div>
            </div>
          </div>

          <div
            v-else
            class="mb-5 flex w-full flex-col items-start"
          >
            <div
              class="mb-2 w-full border-b-2 border-light-gray text-sm text-body-color"
            >
              <p class="pb-1">W9 Information</p>
            </div>
            <div class="flex flex-col md:flex-row md:gap-x-6">
              <div class="mb-3">
                <div class="flex-col">
                  <p class="categoryParagraph">TIN</p>
                  <p
                    v-if="data.contracts_information?.tin_value"
                    class="text-dark-gray"
                  >
                    {{ data.contracts_information?.tin_value }} ({{
                      data.contracts_information?.tin_type?.toUpperCase()
                    }})
                  </p>
                </div>
              </div>
            </div>
            <div class="mb-3 flex-col">
              <p class="categoryParagraph">Federal Tax Classification</p>
              <p class="text-dark-gray">
                {{ llcType }}
              </p>
            </div>
            <div
              v-if="taxClassification"
              class="mb-3 flex-col"
            >
              <p class="categoryParagraph">Company Tax Classification</p>
              <p class="text-dark-gray">
                {{ taxClassification }}
              </p>
            </div>
          </div>
        </template>
      </div>
      <div
        v-if="show_w8 == true"
        class="mb-5 flex flex-col items-start"
      >
        <template v-if="isW8Optional && !submitW8Anyway">
          <h4 class="mb-8 mt-4 text-left text-dark-gray sm:mt-2">
            {{
              brokerName + ' does not require carriers to complete a W-8 form.'
            }}
          </h4>
        </template>
        <template v-else>
          <h4 class="mb-8 mt-4 text-left text-dark-gray sm:mt-2">
            {{
              firstTime
                ? 'Please provide a tax identification number and federal tax classification for your W-8 Form.'
                : 'Review your tax identification number and federal tax classification for your W-8 Form.'
            }}
          </h4>
          <div
            v-if="firstTime"
            class="w-full"
          >
            <div
              class="w-full border-b-2 border-light-gray text-sm text-body-color"
            >
              <div class="flex gap-2">
                <p class="pb-1">W-8 Information</p>
                <div
                  class="cursor-pointer text-yellow-500 hover:underline"
                  @click="onShowW9ByClick"
                >
                  Complete W-9 instead
                </div>
              </div>
            </div>
            <div class="my-3 flex w-full items-start gap-x-4">
              <div class="flex w-full items-start">
                <div>
                  <dropdown
                    :options="taxNumberTypeOptions"
                    :disabled="taxNumberTypeOptions.length <= 1"
                    :default="
                      altTins?.includes(tinType)
                        ? tinType
                        : tinType == 'ssn'
                          ? 'ssn'
                          : tinType == 'ein'
                            ? 'ein'
                            : !$store.state.company?.companyData
                                  ?.allowed_tins ||
                                $store.state.company?.companyData?.allowed_tins.includes(
                                  'rfc'
                                )
                              ? 'rfc'
                              : 'ftin'
                    "
                    bg-class="bg-yellow-500 text-white"
                    :light-text="true"
                    @dropdown-input="onSelectTaxNumberType"
                  />
                </div>
                <div class="ml-4 flex-1 self-start">
                  <input
                    v-if="selectedTaxNumberOption?.format"
                    id="taxNumber"
                    v-model="taxNumber"
                    v-mask="selectedTaxNumberOption.format"
                    class="input-padding w-full rounded border text-1md text-sm focus:text-dark-gray"
                    :class="[
                      validateIdentifier(
                        selectedTaxNumberOption?.slug,
                        taxNumber
                      ) == false
                        ? 'failed-validation border-red-500 bg-red-100 text-red-500 focus:border-red-500 focus:bg-red-100 focus:text-red-500'
                        : 'border-transparent bg-off-white text-body-color focus:bg-white',
                    ]"
                    type="text"
                    placeholder="Enter tax identification number"
                    :name="tax_number + '' + tinType"
                    :disabled="isTaxIdentificationSaved"
                  />
                  <input
                    v-else
                    id="taxNumber"
                    v-model="taxNumber"
                    class="input-padding w-full rounded border text-1md text-sm focus:text-dark-gray"
                    :class="[
                      validateIdentifier(
                        selectedTaxNumberOption?.slug,
                        taxNumber
                      ) == false
                        ? 'failed-validation border-red-500 bg-red-100 text-red-500 focus:border-red-500 focus:bg-red-100 focus:text-red-500'
                        : 'border-transparent bg-off-white text-body-color focus:bg-white',
                    ]"
                    type="text"
                    placeholder="Enter tax identification number"
                    :name="tax_number + '' + tinType"
                    :disabled="isTaxIdentificationSaved"
                  />
                  <div
                    v-if="
                      validateIdentifier(
                        selectedTaxNumberOption?.slug,
                        taxNumber
                      ) == false
                    "
                    class="pl-1 pt-0.5 text-left text-2xs text-red-500"
                  >
                    Invalid entry:
                    {{ validationMessage?.[selectedTaxNumberOption?.slug] }}
                  </div>
                </div>
              </div>
            </div>
            <div class="flex w-full gap-x-4 pt-3">
              <div class="w-full">
                <div class="flex items-start">
                  <p class="dark-blue">
                    Please select the applicable W-8 Form below:
                  </p>
                  <tax-tool-tip-large
                    color-scheme="light"
                    :content="tax_form_selection_tooltip"
                    class="relative"
                    style="top: 1.5px"
                  >
                    <div class="tooltipStyle">
                      <div v-html="tax_form_selection_tooltip" />
                    </div>
                  </tax-tool-tip-large>
                </div>
                <template v-if="w8OptionsLoaded">
                  <toggle-button
                    :items="w8Option"
                    :initial-value="defaultW8Option"
                    size="lg"
                    container-class="my-3"
                    :active-toggle-bg-yellow="true"
                    @selected-value="handleDropdownSelectW8Type"
                  />
                </template>
              </div>
            </div>
            <div class="my-3 w-full flex-col">
              <p
                class="categoryParagraph"
                style="margin-bottom: 0.5rem"
              >
                {{ w8TaxFormText !== 'FORM-8233' ? 'Form ' : ''
                }}{{ w8TaxFormText
                }}{{
                  w8TaxFormText == 'W-8BEN-E'
                    ? ' (Non-US Entities)'
                    : w8TaxFormText == 'W-8BEN'
                      ? ' (Non-US Individuals)'
                      : ''
                }}
              </p>
              <!-- <div v-if="!editTaxForm && currentTaxFormPreviewUrl && w8Type == latestTaxFormType && taxQuestionSections?.length > 1" class="flex flex-col gap-y-2 pb-2">
                <div
                  v-for="(section, index) in taxQuestionSections"
                  :key="index"
                >
                  <vue-collapsible-panel class="rounded-md border border-light-gray shadow-black" :expanded="true">
                    <template #title>
                      <div class="text-sm px-4 py-3 text-left flex gap-3 items-center">
                        <div>
                          <div class="py-1 px-2 bg-placeholder-gray text-dark-gray uppercase rounded-md text-2xs whitespace-nowrap">{{ section?.part_label }}</div>
                        </div>
                        <span class="text-dark-gray">{{ section?.name }}</span>
                      </div>
                    </template>
                    <template #content>
                      <div class="hidden"><div class="hidden"></div></div>
                      <div class="pb-4" v-if="section?.tax_questions?.length > 0">
                        <div
                          class="text-left px-6 pt-4"
                          v-for="(question, index) in section?.tax_questions"
                          :key="index"
                        >
                          <div class="w-full flex flex-col gap-y-2">
                            <div class="flex w-full gap-x-1 justify-start items-start md:pr-8">
                              <div class="w-6 text-dark-gray text-xs leading-tight font-bold"><span v-if="question?.question_label" class="relative" style="top: 0.5px;">{{ question?.question_label }}.</span></div>
                              <div class="flex-1 flex flex-col">
                                <div v-if="question?.is_html_subject" class="text-xs leading-tight text-dark-gray tax-form-display">
                                  <component v-bind:is="processedHtml(question)"></component>
                                </div>
                                <div v-else class="text-dark-gray text-xs leading-tight">{{ question?.subject }}</div>
                                <div v-if="question?.is_html_description" v-html="question?.description" class="text-placeholder-gray text-xs leading-tight"></div>
                                <div v-else class="text-gray-over-dark text-2xs uppercase leading-tight">{{ question?.description }}</div>
                              </div>
                            </div>
                            <div v-if="question?.is_type !== 'group' && question?.is_inline !== true" class="flex w-full gap-x-1 justify-start items-start pr-8">
                              <div class="w-6"></div>
                              <div class="flex-1 flex flex-col">
                                <div class="text-body-color text-xs leading-tight">{{ getTaxFormAnswerByQuestionId(question) }}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </vue-collapsible-panel>
                </div>
              </div> -->
              <div
                v-if="taxQuestionSections?.length > 0"
                class="flex flex-col gap-y-2 pb-2"
              >
                <div
                  v-for="(section, index) in taxQuestionSections"
                  :key="`${section?.id}-${index}`"
                >
                  <vue-collapsible-panel
                    v-if="section?.tax_form_is_type == currentTaxForm"
                    class="rounded-md border border-light-gray shadow-black"
                    :class="[
                      section?.tax_form_is_type == currentTaxForm
                        ? 'shown-tax-question-section-wrap'
                        : '',
                    ]"
                    :expanded="true"
                  >
                    <template #title>
                      <div class="hidden">
                        <div class="hidden">
                          {{ section?.id }}
                        </div>
                      </div>
                      <div
                        class="flex items-center gap-3 px-4 py-3 text-left text-sm"
                      >
                        <div>
                          <div
                            class="whitespace-nowrap rounded-md bg-placeholder-gray px-2 py-1 text-2xs uppercase text-dark-gray"
                          >
                            {{ section?.part_label }}
                          </div>
                        </div>
                        <span class="text-dark-gray">{{ section?.name }}</span>
                      </div>
                    </template>
                    <template #content>
                      <div class="hidden">
                        <div class="hidden">
                          {{ section?.id }}
                        </div>
                      </div>
                      <div
                        v-if="section?.tax_questions?.length > 0"
                        class="pb-4"
                      >
                        <div
                          v-for="(question, index) in section?.tax_questions"
                          :key="index"
                          class="px-6 pt-4 text-left"
                        >
                          <dynamic-tax-question-form
                            v-if="section?.tax_form_is_type == currentTaxForm"
                            :item="question"
                            :toggle-required="toggleRequired"
                            :hidden="hidden"
                            :hide-hidden="hideHidden"
                            :unhide-hidden="unhideHidden"
                            :latest-tax-form-type="latestTaxFormType"
                            :current-answer-object="
                              getTaxFormAnswerObjectByQuestionId(question)
                            "
                            @hide="hideHidden"
                            @unhide="unhideHidden"
                          />
                        </div>
                      </div>
                    </template>
                  </vue-collapsible-panel>
                </div>
              </div>
              <div
                v-else
                class="flex w-full flex-col gap-y-2 pb-2"
              >
                <div
                  v-for="index in 3"
                  :key="index"
                  class="force-inline-flex loading-gradient rounded-lg"
                >
                  <div class="block h-12 w-full rounded-lg p-3" />
                </div>
              </div>
              <!-- <div class="flex flex-col gap-2">
                <round-radio-button
                  v-for="item in items.federal_tax_class"
                  :key="item"
                  :item="item.name"
                  :handleClick="($event) => handleSelectRadio($event, item.value)"
                  name="federal_tax_class"
                  :value="item.name"
                  :checked="data.federal_tax_class === item.value"
                ></round-radio-button>
              </div>
              <div v-if="selected">
                <dropdown-select
                  :options="selectOptions"
                  :default="'Enter C, S or P'"
                  @input="handleDropdownSelect"
                  label="Tax Classification (Required)"
                  class="text-sm"
                ></dropdown-select>
                <span class="w-80 md:w-full flex">
                  <p class="text-body-color text-2xs pt-0">Note: Limited Liability Company Tax Classifications (C = C Corporation, S = S Corporation, P = Partnership)</p>
                </span>
              </div> -->
            </div>
            <!-- <div v-if="!editTaxForm && currentTaxFormPreviewUrl && w8Type == latestTaxFormType">
              <div class="w-48 mx-auto pt-4">
                <button-main
                  @click="editTaxForm = true"
                  class="my-2 md:my-0 md:mb-0"
                  variant="secondary"
                  :disabled="false"
                  type="button"
                  :autoFocus="true"
                  :loadingSpinner="false"
                >
                  Edit {{ w8TaxFormText }}
                </button-main>
              </div>
            </div> -->
            <!-- <div class="w-48 mx-auto pt-4">
              <button-main
                @click="handleTaxFormSubmit"
                class="my-2 md:my-0 md:mb-0"
                :disabled="taxFormSubmitButtonLoading"
                type="button"
                :autoFocus="false"
                :isLoading="taxFormSubmitButtonLoading"
              >
                Submit {{ w8TaxFormText }}
              </button-main>
            </div> -->
          </div>

          <div
            v-else
            class="mb-5 flex w-full flex-col items-start"
          >
            <div
              class="mb-2 w-full border-b-2 border-light-gray text-sm text-body-color"
            >
              <p class="pb-1">W8 Information</p>
            </div>
            <div class="flex flex-col md:flex-row md:gap-x-6">
              <div class="mb-3">
                <div class="flex-col">
                  <p class="categoryParagraph">TIN</p>
                  <p
                    v-if="data.contracts_information?.tin_value"
                    class="text-dark-gray"
                  >
                    {{ data.contracts_information?.tin_value }} ({{
                      data.contracts_information?.tin_type?.toUpperCase()
                    }})
                  </p>
                </div>
              </div>
            </div>
            <div class="mb-3 flex-col">
              <p class="categoryParagraph">Federal Tax Classification</p>
              <p class="capitalize text-dark-gray">
                {{
                  currentTaxClassification == 'foreign'
                    ? 'Foreign Entity or Individual'
                    : currentTaxClassification
                }}
              </p>
            </div>
            <p
              class="categoryParagraph"
              style="margin-bottom: 0.5rem"
            >
              Form {{ w8TaxFormText }} Responses
            </p>
            <div
              v-if="
                latestTaxFormType &&
                latestTaxFormType !== 'w_9' &&
                latestTaxSubmissionTaxQuestionSections?.length > 0
              "
              class="flex w-full flex-col gap-y-2 pb-2"
            >
              <div
                v-for="(
                  section, index
                ) in latestTaxSubmissionTaxQuestionSections"
                :key="`${section?.id}-${index}`"
              >
                <vue-collapsible-panel
                  class="rounded-md border border-light-gray shadow-black"
                  :expanded="true"
                >
                  <template #title>
                    <div
                      class="flex items-center gap-3 px-4 py-3 text-left text-sm"
                    >
                      <div>
                        <div
                          class="whitespace-nowrap rounded-md bg-placeholder-gray px-2 py-1 text-2xs uppercase text-dark-gray"
                        >
                          {{ section?.part_label }}
                        </div>
                      </div>
                      <span class="text-dark-gray">{{ section?.name }}</span>
                    </div>
                  </template>
                  <template #content>
                    <div class="hidden">
                      <div class="hidden" />
                    </div>
                    <div
                      v-if="section?.tax_questions?.length > 0"
                      class="pb-4"
                    >
                      <div
                        v-for="(question, index) in section?.tax_questions"
                        :key="index"
                        class="px-6 pt-4 text-left"
                      >
                        <div class="flex w-full flex-col gap-y-2">
                          <div
                            class="flex w-full items-start justify-start gap-x-1 md:pr-8"
                          >
                            <div
                              class="w-8 flex-shrink-0 whitespace-nowrap text-xs font-bold leading-tight text-dark-gray"
                            >
                              <span
                                v-if="question?.question_label"
                                class="relative"
                                style="top: 0.5px"
                                >{{ question?.question_label }}.</span
                              >
                            </div>
                            <div class="flex flex-1 flex-col">
                              <div
                                v-if="question?.is_html_subject"
                                class="tax-form-display text-xs leading-tight text-dark-gray"
                              >
                                <component
                                  :is="dynamicComponentByType['html']"
                                  :processed-html="processedHtml(question)"
                                />
                              </div>
                              <div
                                v-else
                                class="text-xs leading-tight text-dark-gray"
                              >
                                {{ question?.subject }}
                              </div>
                              <div
                                v-if="question?.is_html_description"
                                class="text-xs leading-tight text-placeholder-gray"
                                v-html="question?.description"
                              />
                              <div
                                v-else
                                class="text-2xs uppercase leading-tight text-gray-over-dark"
                              >
                                {{ question?.description }}
                              </div>
                            </div>
                          </div>
                          <div
                            v-if="
                              question?.is_type !== 'group' &&
                              question?.is_inline !== true
                            "
                            class="flex w-full items-start justify-start gap-x-1 pr-8"
                          >
                            <div class="w-8" />
                            <div class="flex flex-1 flex-col">
                              <div
                                class="text-xs leading-tight text-body-color"
                              >
                                {{ getTaxFormAnswerByQuestionId(question) }}
                              </div>
                            </div>
                          </div>
                          <template v-else>
                            <div
                              v-for="child in question?.child_questions"
                              :key="child?.id"
                              class="flex w-full items-start justify-start gap-x-1 pr-8"
                            >
                              <div class="w-8" />
                              <div class="flex flex-1">
                                <div
                                  class="text-xs leading-tight text-body-color"
                                >
                                  <span
                                    v-if="
                                      child?.show_subject == true &&
                                      !question?.subject
                                        ?.toLowerCase()
                                        ?.includes('address')
                                    "
                                    class="text-xs italic leading-tight text-body-color"
                                    >{{ child?.subject }}: </span
                                  >{{ getTaxFormAnswerByQuestionId(child) }}
                                </div>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </template>
                </vue-collapsible-panel>
              </div>
            </div>
            <div
              v-else
              class="flex w-full flex-col gap-y-2 pb-2"
            >
              <div
                v-for="index in 3"
                :key="index"
                class="force-inline-flex loading-gradient rounded-lg"
              >
                <div class="block h-12 w-full rounded-lg p-3" />
              </div>
            </div>
            <!-- <div
              v-if="taxClassification"
              class="flex-col mb-3"
            >
              <p class="categoryParagraph">
                Company Tax Classification
              </p>
              <p class="text-dark-gray">{{ taxClassification }}</p>
            </div> -->
          </div>
        </template>
      </div>
    </div>
    <form-navigation-buttons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      :is-button-disabled="isButtonDisabled"
      :use-internal-button-disabled-logic="true"
      :tax-form-title="w8TaxFormText"
      @next="handleNext"
      @back="handleBack"
      @edit="handleEdit"
      @w8-submit="handleW8Submit"
      @exit-modal="emitExitModal"
      @bypass-w9="handleBypassW9"
      @complete-w9-anyway="handleCompleteW9Anyway"
      @view-w8="handleViewW8"
      @bypass-w8="handleBypassW8"
      @complete-w8-anyway="handleCompleteW8Anyway"
      @view-w9="handleViewW9"
    />
  </div>
</template>

<script>
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import { useFormRouter } from '@/composables/formRouter';
import { useStore } from 'vuex';
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeMount,
  shallowRef,
} from 'vue';
import stepInformation from '@/common/stepInformation';
import DropdownSelect from '@/components/DropdownSelect.vue';
import RoundRadioButton from '@/components/RoundRadioButton.vue';
import ToggleButton from '@/components/ToggleButton.vue';
import DynamicTaxQuestionForm from './DynamicTaxQuestionForm.vue';
import _ from 'lodash';
import Dropdown from '@/components/Dropdown.vue';
import TaxToolTipLarge from '@/components/TaxToolTipLarge.vue';
import { VueCollapsiblePanel } from '@dafcoe/vue-collapsible-panel';
import ButtonMain from '@/components/Button.vue';
// import { arrayTypeAnnotation } from "@babel/types";
import dayjs from 'dayjs';
import { toastResponseInSetup } from '@/common/handleServerResponse';
// const { inject } = Vue;
import { useToast } from 'vue-toast-notification';
import ProcessedHtml from './Type/ProcessedHtml.vue';

const processedHtmlRef = shallowRef(ProcessedHtml);

const TaxNumberTypes = {
  BN: 'bn',
  RFC: 'rfc',
  GIIN: 'giin',
  FTIN: 'ftin',
  SIN: 'sin',
  EIN: 'ein',
  SSN: 'ssn',
};

export default {
  name: 'ContractStepOne',
  components: {
    FormNavigationButtons,
    ToggleButton,
    RoundRadioButton,
    DropdownSelect,
    DynamicTaxQuestionForm,
    Dropdown,
    TaxToolTipLarge,
    VueCollapsiblePanel,
    ButtonMain,
  },
  props: {
    data: Object,
    formPosition: Number,
    title: String,
    updateField: Function,
    nextStep: Function,
    stepName: String,
  },
  setup(props, { emit }) {
    // const vueApp = inject('vueAppReference');
    const toast = useToast();

    const store = useStore();
    const componentIdentifier = 'Contract-1';
    const formRouter = useFormRouter(componentIdentifier);
    const isNavigationDisabled = computed(() => formRouter.isDisabled.value);
    const firstTime = computed(
      () =>
        store.state.firstTime?.[
          formRouter.componentStep.value?.firstTimeIdentifier
        ]
    );
    const isW9Optional = computed(
      () =>
        store.state.carriers?.carrierData?.connection?.issuer?.is_w9_optional
    );
    const isW8Optional = computed(
      () =>
        store.state.carriers?.carrierData?.connection?.issuer?.is_w8_optional
    );
    const submitW9Anyway = ref(false);
    const submitW8Anyway = ref(false);

    const currentTaxFormVal = computed(
      () => store.state.taxQuestions?.current_tax_form || null
    );
    const taxFormTitle = computed(() => {
      if (currentTaxFormVal.value && currentTaxFormVal.value !== 'w_9') {
        const titles = {
          form_8233: 'Form 8233',
          w_8ben: 'W-8BEN',
          w_8ben_e: 'W-8BEN-E',
          w_8ben_e_substitute: 'W-8BEN-E(S)',
          w_8eci: 'W-8ECI',
        };
        return titles[currentTaxFormVal.value];
      } else {
        return null;
      }
    });

    const buttonCheck1 = computed(() => firstTime.value && taxFormTitle.value);
    const buttonsToDisplay = computed(() => {
      if (isW8Optional.value && show_w8.value && !submitW8Anyway.value)
        return 'optional-w8';
      if (isW9Optional.value && !show_w8.value && !submitW9Anyway.value)
        return 'optional-w9';
      if (buttonCheck1.value) return 'submit-save';
      if (!firstTime.value) return 'continue-edit';
      return 'continue-save';
    });
    const isButtonDisabled = ref(false);

    const company_question_sections = computed(
      () => store.state.carriers.carrierData?.company_question_sections || []
    );
    const broker_connect_channels = computed(
      () => store.state.onboardingPacket?.broker_connect_channels || []
    );
    const auto_skip_stage_six = computed(
      () => store.state.carriers.carrierData?.auto_skip_stage_six || false
    );
    const auto_skip_stage_seven = computed(
      () => store.state.carriers.carrierData?.auto_skip_stage_seven || false
    );
    const auto_skip_stage_eight = computed(
      () => store.state.carriers.carrierData?.auto_skip_stage_eight || false
    );
    // const auto_skip_stage_nine = computed(() => store.state.carriers.carrierData?.auto_skip_stage_nine || false);

    const setQuestionSectionIndexToLast = () => {
      store.commit(
        'questions/setSection',
        store.state.questions.total_sections
      );
      store.commit(
        'questions/setSectionIndex',
        store.state.questions.total_sections - 1
      );
    };

    const handleNext = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleNext' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.moveToNextStep();
    };

    // backChecks: Checks to be reviewed when the user clicks the Back button (but before any store.state.formRoutes.activeStep changes)
    const backCheck1 = computed(
      () =>
        (!broker_connect_channels.value?.length ||
          auto_skip_stage_eight.value) &&
        (!company_question_sections.value?.length ||
          auto_skip_stage_seven.value) &&
        auto_skip_stage_six.value
    );
    const backCheck2 = computed(
      () =>
        (!broker_connect_channels.value?.length ||
          auto_skip_stage_eight.value) &&
        (!company_question_sections.value?.length ||
          auto_skip_stage_seven.value) &&
        (store.state.payments.payment_type === 'factoring' ||
          !!store.state.carriers.carrierData?.payment_method)
    );
    const backCheck3 = computed(
      () =>
        (!broker_connect_channels.value?.length ||
          auto_skip_stage_eight.value) &&
        (!company_question_sections.value?.length ||
          auto_skip_stage_seven.value)
    );
    const backCheck4 = computed(
      () =>
        (!broker_connect_channels.value?.length ||
          auto_skip_stage_eight.value) &&
        company_question_sections.value?.length > 0
    );

    const handleBack = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleBack' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );

      if (backCheck1.value) {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck1 is TRUE (auto_skip_stages: 6 is true, 7 is true (or no company question sections), 8 is true (or no broker_connect_channels)), skipping Integrations, Questions, and Payment steps and moving to Insurance-4'
        );
        return formRouter.moveToStepByIdentifier('Insurance-4');
      } else {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck1 is FALSE (Not skipping Integrations, Questions, and Payment steps)'
        );
      }

      if (backCheck2.value) {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck2 is TRUE (auto_skip_stages: 7 is true (or no company question sections), 8 is true (or no broker_connect_channels), and either payment_type is factoring or a payment_method exists), skipping Integrations and Questions and moving to Payment-2'
        );
        return formRouter.moveToStepByIdentifier('Payment-2');
      } else {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck2 is FALSE (Not skipping Integrations and Questions to Payment-2)'
        );
      }

      if (backCheck3.value) {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck3 is TRUE (auto_skip_stages: 6 is true, 7 is true (or no company question sections), 8 is true (or no broker_connect_channels)), skipping Integrations and Questions and moving to Payment-2'
        );
        return formRouter.moveToStepByIdentifier('Payment-2');
      } else {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck3 is FALSE (Not skipping Integrations and Questions to Payment-2)'
        );
      }

      if (backCheck4.value) {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck4 is TRUE (auto_skip_stages: 8 is true (or no broker_connect_channels), has company question sections), skipping Integrations and moving to Questions'
        );
        setQuestionSectionIndexToLast();
        return formRouter.moveToStepByIdentifier(
          shouldEnableCHRFlow.value ? 'Ownership-2' : 'Questions'
        );
      } else {
        store.commit(
          'addFormDebugMessage',
          'BACK CHECK | ' +
            componentIdentifier +
            ': backCheck4 is FALSE (Not skipping Integrations to Questions)'
        );
      }

      formRouter.moveToPreviousStep();
    };

    const handleEdit = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleEdit' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.editActiveStep();
    };

    const shouldEnableCHRFlow = computed(
      () => store.getters.shouldEnableCHRFlow
    );

    // redirectChecks: Checks for possible redirects that need to be reviewed immediately when the user lands on this step (specifically, when store.state.formRoutes.activeStep.identifier == componentIdentifier)
    const redirectCheck1 = computed(() => {
      // auto_skip_stage_nine Redirect Check
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ': Redirect check (auto_skip_stage_nine)'
      );
      if (store.state.carriers?.carrierData?.auto_skip_stage_nine) {
        store.commit(
          'addFormDebugMessage',
          'REDIRECT | ' +
            componentIdentifier +
            ': Redirect needed (auto_skip_stage_nine), will attempt to complete packet'
        );
        return 'AttemptToComplete';
      }
      return false;
    });

    const handleRedirect = (val) => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleRedirect' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.moveToStepByIdentifier(val);
    };

    watch(isNavigationDisabled, (val) => {
      if (val == false) {
        store.commit(
          'addFormDebugMessage',
          'CHECK | ' +
            componentIdentifier +
            ': (isNavigationDisabled watcher) ' +
            componentIdentifier +
            ' is active step, running redirect checks'
        );
        if (redirectCheck1.value !== false) {
          handleRedirect(redirectCheck1.value);
        }
        console.log(
          'calling updateIsButtonDisabled from isNavigationDisabled watcher'
        );
        updateIsButtonDisabled();
      }
    });

    const preNextCheck1 = () => {
      // check for required fields in w_8ben_e_substitute
      if (currentTaxFormVal.value !== 'w_8ben_e_substitute') {
        return false;
      }
      const w8_answers =
        store.state.taxQuestions?.tax_form_submission?.tax_answers_attributes
          ?.length > 0
          ? [
              ...(store.state.taxQuestions?.tax_form_submission
                ?.tax_answers_attributes ?? []),
            ]
          : [];

      let req1 = w8_answers.find((answer) => answer?.tax_question_id == 233);
      let req2 = w8_answers.find((answer) => answer?.tax_question_id == 237);
      let req3 = w8_answers.find((answer) => answer?.tax_question_id == 240);
      let req4 = w8_answers.find((answer) => answer?.tax_question_id == 241);
      let req5 = w8_answers.find((answer) => answer?.tax_question_id == 242);
      let req6 = w8_answers.find((answer) => answer?.tax_question_id == 253);
      let certificationCheckbox = w8_answers.find(
        (answer) => answer?.tax_question_id == 263
      );
      let signerName = w8_answers.find(
        (answer) => answer?.tax_question_id == 264
      );
      if (
        certificationCheckbox?.tax_answer_options_attributes?.[0]
          ?.tax_question_option_id == 165 &&
        signerName?.string_value?.length > 0 &&
        req1?.string_value?.length > 0 &&
        req2?.tax_answer_options_attributes?.[0]?.tax_question_option_id >=
          136 &&
        req2?.tax_answer_options_attributes?.[0]?.tax_question_option_id <=
          148 &&
        req3?.string_value?.length > 0 &&
        req4?.string_value?.length > 0 &&
        req5?.string_value?.length > 0 &&
        req6?.tax_answer_options_attributes?.[0]?.tax_question_option_id >=
          154 &&
        req6?.tax_answer_options_attributes?.[0]?.tax_question_option_id <= 164
      ) {
        return false;
      } else {
        return true;
      }
    };

    const handleW8Submit = async () => {
      console.log('handleW8Submit');
      let check = preNextCheck1();
      if (check) {
        toast.open({
          message:
            'Please complete all required fields for the W-8BEN-E(S) form',
          type: 'error',
          position: 'top-right',
          duration: 5000,
        });
        store.commit('taxQuestions/setShowValidationErrors', {
          is_type: currentTaxFormVal.value,
          bool: true,
        });
        store.commit(
          'addFormDebugMessage',
          'FAILED VALIDATION: ' +
            componentIdentifier +
            ': required fields are missing for W-8BEN-E(S), showing toast error and returning'
        );
        return;
      }
      // this.triggerLoadingBtn(true);
      let res = await store.dispatch('taxQuestions/submitTaxForm');
      toastResponseInSetup(this, res, 'Tax form successfully submitted');
      if ([200, 201].includes(res?.status)) {
        store.commit('firstTime/updateStepFirstTime', {
          step: 'Contract',
          first_time: false,
        });
      }
      // this.triggerLoadingBtn(false);
    };

    const firstTimeVal = firstTime;
    const show_w8 = ref(false);
    const isTaxIdentificationSaved = ref(false);
    const tax_number = ref('');
    const w8Type = ref('w_8ben');
    const einSsnToggleButton = ref(null);
    const taxFormSubmitButtonLoading = ref(false);
    // const latestTaxFormType = computed(() => store.state.carriers?.carrierData?.latest_tax_form_submission?.tax_form?.is_type || null);
    // const latestTaxFormAnswers = computed(() => {
    //   if (store.state.carriers?.carrierData?.latest_tax_form_submission?.tax_answers?.length > 0 &&
    //       Array.isArray(store.state.carriers?.carrierData?.latest_tax_form_submission?.tax_answers)) {
    //         return [ ...store.state.carriers?.carrierData?.latest_tax_form_submission?.tax_answers ];
    //      }
    //   return [];
    // });
    // const latestTaxFormType = computed(() => store.state.taxQuestions?.latest_company_tax_form_submission?.tax_form?.is_type || null);
    // const latestTaxFormAnswers = computed(() => {
    //   if (store.state.taxQuestions?.latest_company_tax_form_submission?.tax_answers?.length > 0 &&
    //       Array.isArray(store.state.taxQuestions?.latest_company_tax_form_submission?.tax_answers)) {
    //         return [ ...store.state.taxQuestions?.latest_company_tax_form_submission?.tax_answers ];
    //      }
    //   return [];
    // });
    const latestTaxFormType = computed(
      () => store.state.taxQuestions?.current_tax_form || null
    );
    const latestTaxFormAnswers = computed(() => {
      if (
        store.state.taxQuestions?.latest_company_tax_form_submissions?.[
          latestTaxFormType?.value
        ]?.tax_answers?.length > 0 &&
        Array.isArray(
          store.state.taxQuestions?.latest_company_tax_form_submissions?.[
            latestTaxFormType?.value
          ]?.tax_answers
        )
      ) {
        return [
          ...(store.state.taxQuestions?.latest_company_tax_form_submissions?.[
            latestTaxFormType?.value
          ]?.tax_answers ?? []),
        ];
      }
      return [];
    });
    const allowedW8IsTypes = computed(
      () =>
        store.state.carriers?.carrierData?.connection?.issuer
          ?.allowed_w8_is_types ||
        store.state.company?.companyData?.allowed_w8_is_types
    );

    const currentTaxFormPreviewUrl = computed(
      () =>
        store.state.taxQuestions?.latest_company_tax_form_submission
          ?.tax_form_contract_preview?.url || null
    );
    const latestCompanyTaxFormSubmission = computed(
      () => store.state.taxQuestions?.latest_company_tax_form_submission || null
    );
    const latestCompanyTaxFormSubmissionTaxFormIsType = computed(
      () =>
        store.state.taxQuestions?.latest_company_tax_form_submission
          ?.tax_form_is_type || null
    );

    const allowedTins = computed(
      () => store.state.company?.companyData?.allowed_tins
    );

    const altTinTypeOptions = ['bn', 'rfc', 'giin', 'ftin', 'sin'];

    const altTinTypes = computed(() => {
      if (allowedTins.value?.length > 0) {
        return altTinTypeOptions.filter((option) =>
          allowedTins.value?.includes(option)
        );
      } else {
        return altTinTypeOptions;
      }
    });

    const identifierOptions = [
      {
        label: 'BN',
        slug: 'bn',
        id: TaxNumberTypes.BN,
        format: 'XXXXXXXXX',
      },
      {
        label: 'RFC',
        slug: 'rfc',
        id: TaxNumberTypes.RFC,
        format: 'XXXXXXXXXXXXX',
      },
      {
        label: 'GIIN',
        slug: 'giin',
        id: TaxNumberTypes.GIIN,
        format: 'XXXXXX.XXXXX.XX.XXX',
      },
      {
        label: 'FTIN',
        slug: 'ftin',
        id: TaxNumberTypes.FTIN,
      },
      {
        label: 'SIN',
        slug: 'sin',
        id: TaxNumberTypes.SIN,
        format: 'XXX-XXX-XXX',
      },
      {
        label: 'EIN',
        slug: 'ein',
        id: TaxNumberTypes.EIN,
        format: 'XX-XXXXXXX',
      },
      {
        label: 'SSN',
        slug: 'ssn',
        id: TaxNumberTypes.SSN,
        format: 'XXX-XX-XXXX',
      },
    ];

    const taxNumberTypeOptions = computed(() => {
      if (allowedTins.value?.length > 0) {
        return identifierOptions.filter((option) =>
          allowedTins.value?.includes(option.slug)
        );
      } else {
        return identifierOptions;
      }
    });

    // const ein = computed({
    //   set: function (value) {
    //     const updatedParams = {
    //       tin_value: value,
    //       tin_type: tinType.value,
    //     };
    //     const updatedValue = {
    //       ...props.data.contracts_information,
    //       ...updatedParams,
    //     };
    //     props.updateField("contracts_information", updatedValue);
    //   },
    //   get: function () {
    //     return props.data.contracts_information.tin_value;
    //   }
    // });

    // const ssn = computed({
    //   set: function (value) {
    //     const updatedParams = {
    //       tin_value: value,
    //       tin_type: tinType.value,
    //     };
    //     const updatedValue = {
    //       ...props.data.contracts_information,
    //       ...updatedParams,
    //     };
    //     props.updateField("contracts_information", updatedValue);
    //   },
    //   get: function () {
    //     return props.data.contracts_information.tin_value;
    //   }
    // });

    const taxNumber = computed({
      set: function (value) {
        const updatedParams = {
          tin_value: value,
          tin_type: tinType.value,
        };
        const updatedValue = {
          ...props.data.contracts_information,
          ...updatedParams,
        };
        props.updateField('contracts_information', updatedValue);
        store.commit('taxQuestions/setTaxNumber', value);
      },
      get: function () {
        return props.data.contracts_information.tin_value;
      },
    });

    const selectedTaxNumberOption = computed(() => {
      return taxNumberTypeOptions.value.find(
        (option) => option.id === tinType.value
      );
    });

    const listTaxIndentifiers = computed(
      () => store.state.carriers.carrierData?.identity?.tax_identifiers || ''
    );
    const latestTaxIdentifier = computed(
      () => store.getters['carriers/latestTaxIdentifier'] || null
    );
    const tinValue = computed(
      () => props.data?.contracts_information?.tin_value || ''
    );
    const tinValueWithoutFormat = computed(
      () => tinValue.value.replace(/-/g, '')
      // tinValue.value.replace(/[^a-zA-z0-9]/g, "")
    );

    const hasCheckedForTaxFormSubmission = computed(() => {
      return store.state.taxQuestions?.hasCheckedForTaxFormSubmission || null;
    });

    const physicalAddress = computed(
      () => store.state.carriers.carrierData?.identity?.physical_address || null
    );

    const mailingAddress = computed(
      () => store.state.carriers.carrierData?.identity?.mailing_address || null
    );

    const preferredCountry = computed(() => store.state.users.preferredCountry);

    const hasCanadianAddress = computed(() => {
      return (
        physicalAddress?.value?.nationality === 'CA' ||
        mailingAddress?.value?.nationality === 'CA' ||
        preferredCountry.value === 'ca'
      );
    });

    const defaultTinOption = computed(() => {
      if (
        latestTaxIdentifier?.value &&
        (!store.state.company?.companyData?.allowed_tins ||
          store.state.company?.companyData?.allowed_tins?.includes(
            latestTaxIdentifier?.value?.is_type
          ))
      ) {
        console.dir(latestTaxIdentifier?.value?.is_type?.toUpperCase());
        return latestTaxIdentifier?.value?.is_type?.toUpperCase();
      } else if (
        hasCanadianAddress?.value == true &&
        (!store.state.company?.companyData?.allowed_tins ||
          store.state.company?.companyData?.allowed_tins?.includes('bn'))
      ) {
        // return "FTIN";
        return 'BN';
      }
      return 'EIN';
    });

    const tinType = ref(defaultTinOption?.value?.toLowerCase() ?? 'ein');
    const hasTaxIdentification = ref(
      Object.values(TaxNumberTypes)?.includes(tinType?.value?.toLowerCase())
    );
    const latestTaxSubmissionTaxQuestionSections = computed(
      () =>
        store.state.taxQuestions?.tax_questions?.[latestTaxFormType?.value]?.[0]
          ?.tax_question_sections || []
    );

    const taxFormIsTypeToArray = computed(() => {
      return {
        form_8233: 5,
        w_8ben: 2,
        w_8ben_e: 3,
        w_8ben_e_substitute: 8,
        w_8eci: 4,
        w_9: 1,
      };
    });

    const taxFormIsTypeToDisplayName = computed(() => {
      return {
        form_8233: 'Form 8233',
        w_8ben: 'W-8BEN',
        w_8ben_e: 'W-8BEN-E',
        w_8ben_e_substitute: 'W-8BEN-E(S)',
        w_8eci: 'W-8ECI',
        w_9: 'W-9',
      };
    });

    const existingRecordSource = ref('carrier');

    // If changing defaultW8Option, also change defaultW8OptionIsType
    const defaultW8Option = computed(() => {
      return taxFormIsTypeToDisplayName.value?.[defaultW8OptionIsType.value];
      // if (allowedW8IsTypes.value?.length > 0) {
      //   if (
      //     allowedW8IsTypes.value?.includes(
      //       latestCompanyTaxFormSubmissionTaxFormIsType.value
      //     )
      //   ) {
      //     return taxFormIsTypeToDisplayName.value[
      //       latestCompanyTaxFormSubmissionTaxFormIsType.value
      //     ];
      //   }
      //   return taxFormIsTypeToDisplayName.value[allowedW8IsTypes.value?.[0]];
      // }
      // if (
      //   latestCompanyTaxFormSubmissionTaxFormIsType.value &&
      //   latestCompanyTaxFormSubmissionTaxFormIsType.value !== 'w_9'
      // ) {
      //   return taxFormIsTypeToDisplayName.value[
      //     latestCompanyTaxFormSubmissionTaxFormIsType.value
      //   ];
      // }
      // return 'W-8BEN-E';
    });

    const defaultW8OptionIsType = computed(() => {
      if (allowedW8IsTypes.value?.length > 0) {
        if (
          allowedW8IsTypes.value?.includes(
            latestCompanyTaxFormSubmissionTaxFormIsType.value
          )
        ) {
          return latestCompanyTaxFormSubmissionTaxFormIsType.value;
        }
        return allowedW8IsTypes.value?.[0];
      }
      if (
        latestCompanyTaxFormSubmissionTaxFormIsType.value &&
        latestCompanyTaxFormSubmissionTaxFormIsType.value !== 'w_9'
      ) {
        return latestCompanyTaxFormSubmissionTaxFormIsType.value;
      }
      return 'w_8ben_e';
    });

    const w8TaxFormText = computed(() => {
      return w8Type.value?.toUpperCase().replace(/_/g, '-');
    });

    const isAnExistingRecord = (tinType, tinValue) => {
      const existingRecord = listTaxIndentifiers?.value?.find((tax) => {
        return tax.is_type === tinType && tax.value === tinValue;
      });
      if (existingRecord) {
        existingRecordSource.value = existingRecord?.source;
        return existingRecord.id;
      }
      return -1;
      // if (existingRecord) return existingRecord.id;
      // return -1;
    };

    const updatePacketBody = (body) => {
      if (props.stepName === 'Contract-1') {
        store.dispatch('onboardingPacket/setOnboardingBody', {
          carrier: body,
        });
        if (body?.tax_classification) {
          store.commit(
            'onboardingPacket/setTaxClassification',
            body?.tax_classification
          );
        }
        if (body?.tax_identifiers_attributes) {
          store.commit(
            'onboardingPacket/setTaxIdentifiersAttributes',
            body?.tax_identifiers_attributes
          );
        }
      }
    };

    const setTinBodyParams = () => {
      let tax_identifiers_attributes = [
        {
          value: tinValueWithoutFormat.value,
          is_type: tinType.value,
          source: 'carrier',
        },
      ];
      const existingRecordId = isAnExistingRecord(
        tinType.value,
        tinValueWithoutFormat.value
      );

      if (existingRecordId !== -1) {
        tax_identifiers_attributes = [
          {
            id: existingRecordId,
            value: tinValueWithoutFormat.value,
            is_type: tinType.value,
            source: existingRecordSource.value == 'fmcsa' ? 'fmcsa' : 'carrier',
          },
          // {
          //   id: existingRecordId,
          // },
        ];
      }
      updatePacketBody({
        ...store.state.onboardingPacket.onboardingPacketBody.carrier,
        tax_identifiers_attributes,
      });
    };

    const setTaxClassBodyParams = () => {
      let newTaxClassification = props.data?.federal_tax_class;
      if (
        props.data?.federal_tax_class === 'limited_liability_company' &&
        props.data?.contracts_information.tax_classification !== ''
      ) {
        switch (props.data?.contracts_information.tax_classification) {
          case 'C':
            newTaxClassification = 'llc_c_corporation';
            break;
          case 'S':
            newTaxClassification = 'llc_s_corporation';
            break;
          default:
            newTaxClassification = 'llc_partnership';
            break;
        }
      }
      if (
        show_w8.value == true ||
        altTinTypes.value?.includes(tinType?.value?.toLowerCase())
      ) {
        deleteTaxClassFromBody();
        newTaxClassification = 'foreign';
      }
      updatePacketBody({
        ...store.state.onboardingPacket.onboardingPacketBody.carrier,
        tax_classification: newTaxClassification,
      });
    };

    const deleteTaxClassFromBody = () => {
      const carrier = store.state.onboardingPacket.onboardingPacketBody.carrier;
      if (carrier?.tax_classification) {
        delete carrier.tax_classification;
      }
      updatePacketBody({
        ...carrier,
      });
    };

    const finalizedTaxClassification = computed(() => {
      return store.state.onboardingPacket?.onboardingPacketBody?.carrier
        ?.tax_classification;
    });

    const finalizedTaxIdentifierIsType = computed(() => {
      return store.state.onboardingPacket?.onboardingPacketBody?.carrier
        ?.tax_identifiers_attributes?.[0]?.is_type;
    });

    const finalizedTaxIdentifierValue = computed(() => {
      return store.state.onboardingPacket?.onboardingPacketBody?.carrier
        ?.tax_identifiers_attributes?.[0]?.value;
    });

    const updateIsButtonDisabled = () => {
      console.log('updateIsButtonDisabled');
      let canProceedW9 =
        validateIdentifier(
          finalizedTaxIdentifierIsType.value,
          finalizedTaxIdentifierValue.value
        ) == true && finalizedTaxClassification.value?.length > 0;
      console.log('canProceedW9', canProceedW9);
      if (!canProceedW9) {
        isButtonDisabled.value = true;
        return;
      } else if (show_w8.value == false) {
        isButtonDisabled.value = false;
      } else {
        // show_w8 is true
        console.log('updateIsButtonDisabled 2');
        if (firstTime.value == false) {
          if (
            store.state.taxQuestions?.allW8Tins?.includes(
              props?.data?.contracts_information?.tin_type
            ) &&
            props.data?.federal_tax_class == 'foreign' &&
            store.state.taxQuestions?.latest_company_tax_form_submissions?.[
              store.state.taxQuestions?.current_tax_form
            ]?.tax_answers?.length > 0
          ) {
            isButtonDisabled.value = false;
            return;
          } else {
            // console.log('1 -', store.state.taxQuestions?.allW8Tins?.includes(props?.data?.contracts_information?.tin_value));
            // console.log('2 -', props.data?.federal_tax_class == 'foreign');
            // console.log('3 -', store.state.taxQuestions?.latest_company_tax_form_submissions?.[store.state.taxQuestions?.current_tax_form]?.tax_answers?.length > 0);
            isButtonDisabled.value = true;
            return;
          }
        } else {
          let w8_answers =
            store.state.taxQuestions?.tax_form_submission
              ?.tax_answers_attributes?.length > 0
              ? [
                  ...(store.state.taxQuestions?.tax_form_submission
                    ?.tax_answers_attributes ?? []),
                ]
              : [];
          let certificationCheckbox = null;
          let signerName = null;
          switch (latestTaxFormType.value) {
            case 'w_8ben':
              certificationCheckbox = w8_answers.find(
                (answer) => answer?.tax_question_id == 24
              );
              signerName = w8_answers.find(
                (answer) => answer?.tax_question_id == 23
              );
              if (
                certificationCheckbox?.tax_answer_options_attributes?.[0]
                  ?.tax_question_option_id == 19 &&
                signerName?.string_value?.length > 0
              ) {
                isButtonDisabled.value = false;
                return;
              } else {
                isButtonDisabled.value = true;
                return;
              }
            case 'w_8ben_e':
              certificationCheckbox = w8_answers.find(
                (answer) => answer?.tax_question_id == 189
              );
              signerName = w8_answers.find(
                (answer) => answer?.tax_question_id == 190
              );
              if (
                certificationCheckbox?.tax_answer_options_attributes?.[0]
                  ?.tax_question_option_id == 132 &&
                signerName?.string_value?.length > 0
              ) {
                isButtonDisabled.value = false;
                return;
              } else {
                isButtonDisabled.value = true;
                return;
              }
            case 'w_8ben_e_substitute':
              isButtonDisabled.value = false;
              return;
            // // let req1 = w8_answers.find(
            // //   (answer) => answer?.tax_question_id == 233
            // // );
            // // let req2 = w8_answers.find(
            // //   (answer) => answer?.tax_question_id == 237
            // // );
            // // let req3 = w8_answers.find(
            // //   (answer) => answer?.tax_question_id == 240
            // // );
            // // let req4 = w8_answers.find(
            // //   (answer) => answer?.tax_question_id == 241
            // // );
            // // let req5 = w8_answers.find(
            // //   (answer) => answer?.tax_question_id == 242
            // // );
            // certificationCheckbox = w8_answers.find(
            //   (answer) => answer?.tax_question_id == 263
            // );
            // signerName = w8_answers.find(
            //   (answer) => answer?.tax_question_id == 264
            // );
            // if (
            //   certificationCheckbox?.tax_answer_options_attributes?.[0]
            //     ?.tax_question_option_id == 165 &&
            //   signerName?.string_value?.length > 0
            //   // && req1?.string_value?.length > 0 &&
            //   // req2?.tax_answer_options_attributes?.[0]
            //   //   ?.tax_question_option_id >= 136 &&
            //   // req2?.tax_answer_options_attributes?.[0]
            //   //   ?.tax_question_option_id <= 148 &&
            //   // req3?.string_value?.length > 0 &&
            //   // req4?.string_value?.length > 0 &&
            //   // req5?.string_value?.length > 0
            // ) {
            //   isButtonDisabled.value = false;
            //   return;
            // } else {
            //   isButtonDisabled.value = true;
            //   return;
            // }
            case 'w_8eci':
              certificationCheckbox = w8_answers.find(
                (answer) => answer?.tax_question_id == 46
              );
              signerName = w8_answers.find(
                (answer) => answer?.tax_question_id == 47
              );
              if (
                certificationCheckbox?.tax_answer_options_attributes?.[0]
                  ?.tax_question_option_id == 135 &&
                signerName?.string_value?.length > 0
              ) {
                isButtonDisabled.value = false;
                return;
              } else {
                isButtonDisabled.value = true;
                return;
              }
            case 'form_8233':
              certificationCheckbox = w8_answers.find(
                (answer) => answer?.tax_question_id == 228
              );
              signerName = w8_answers.find(
                (answer) => answer?.tax_question_id == 231
              );
              if (
                certificationCheckbox?.tax_answer_options_attributes?.[0]
                  ?.tax_question_option_id == 134 &&
                signerName?.string_value?.length > 0
              ) {
                isButtonDisabled.value = false;
                return;
              } else {
                isButtonDisabled.value = true;
                return;
              }
          }
        }
      }
    };

    const checkData = () => {
      setTinBodyParams();
      if (
        props.data?.federal_tax_class === 'limited_liability_company' &&
        props.data?.contracts_information?.tax_classification === ''
      ) {
        deleteTaxClassFromBody();
      } else {
        setTaxClassBodyParams();
      }
      console.log('calling updateIsButtonDisabled from checkData func');
      updateIsButtonDisabled();
    };

    const validateIdentifier = (tin_type, value) => {
      let digitsOnlyValue = value?.replace(/\D+/g, '');
      let alphanumericValue = value?.replace(/[^A-Za-z0-9]/g, '');
      switch (tin_type) {
        case 'ein':
          return digitsOnlyValue?.length === 9;
        case 'ssn':
          return digitsOnlyValue?.length === 9;
        case 'bn':
          return digitsOnlyValue?.length === 9;
        case 'rfc':
          /* alphanumeric code, allow letters */
          return (
            alphanumericValue?.length > 11 && alphanumericValue?.length < 14
          );
        case 'ftin':
          return value?.length > 5;
        // return digitsOnlyValue?.length === 9 || value?.replace(/\./g, '')?.length === 16;
        case 'giin':
          // return alphanumericValue?.length == 19;
          return alphanumericValue?.length > 8;
        case 'sin':
          return digitsOnlyValue?.length === 9;
      }
      return false;
    };

    const handleSelectRadio = (e, value) => {
      console.log('handleSelectRadio');
      console.log(e?.target?.name);
      console.log(value);
      if (e?.target?.name) {
        props.updateField(e.target.name, value);
      }
      store.commit('taxQuestions/setFederalTaxClass', value);
      console.log('calling checkData from handleSelectRadio func');
      checkData();
    };

    const handleDropdownSelect = (e) => {
      if (e !== 'Enter C, S or P') {
        const updatedValue = {
          ...props.data.contracts_information,
          tax_classification: e,
        };
        props.updateField('contracts_information', updatedValue);
        store.commit('taxQuestions/setLLCSubClassification', e);
        console.log('calling checkData from handleDropdownSelect func');
        checkData();
      }
    };

    const handleDropdownSelectTinType = (e) => {
      // console.log('handleDropdownSelectTinType');
      // console.log(e);
      if (
        show_w8.value == false &&
        altTinTypes.value?.includes(e.toLowerCase())
      ) {
        tinType.value = 'ein';
      } else {
        tinType.value = e.toLowerCase();
      }
      console.log('calling checkData from handleDropdownSelectTinType func');
      checkData();
      // setTinBodyParams();
    };

    const setCurrentTaxFormAndSubmission = () => {
      if (
        store.state.taxQuestions.current_tax_form !== w8Type.value ||
        store.state.taxQuestions.current_tax_form_id !==
          taxFormIsTypeToArray.value[w8Type.value]
      ) {
        store.commit('taxQuestions/setCurrentTaxForm', {
          is_type: w8Type.value,
          id: taxFormIsTypeToArray.value[w8Type.value],
        });
      }
      if (
        store.state.taxQuestions?.tax_form_submission?.tax_form_id !==
        taxFormIsTypeToArray.value[w8Type.value]
      ) {
        store.commit('taxQuestions/setTaxFormSubmission', {
          prop: 'tax_form_id',
          data: taxFormIsTypeToArray.value[w8Type.value],
        });
      }
    };

    const handleDropdownSelectW8Type = (e) => {
      w8Type.value = e
        ?.toLowerCase()
        ?.trim()
        ?.replaceAll('-', '_')
        ?.replaceAll(' ', '_')
        ?.replaceAll('(s)', '_substitute');
      console.log('setCurrentTaxForm from handleDropdownSelectW8Type');
      setCurrentTaxFormAndSubmission();
    };

    const setUpForm = () => {
      tinType.value = latestTaxIdentifier.value?.is_type;
      let tinVal = latestTaxIdentifier.value?.value;
      const updatedValue = {
        ...props.data.contracts_information,
        tin_value: tinVal,
        tin_type: latestTaxIdentifier.value?.is_type,
      };
      props.updateField('contracts_information', updatedValue);
      props.updateField(
        'federal_tax_class',
        props.data.identity.tax_classification
      );
      setTinBodyParams();
    };

    const onShowW8 = () => {
      show_w8.value = true;
      hasTaxIdentification.value = true;
      if (altTinTypes.value?.includes(latestTaxIdentifier?.value?.is_type)) {
        // console.log('showw8 - 1');
        if (selectedTaxNumberOption?.value?.label !== 'SSN') {
          tinType.value = latestTaxIdentifier?.value?.is_type;
          console.log('calling checkData from onShowW8 func #1');
          checkData();
        }
        return;
      } else {
        // console.log('showw8 - 2');
        let existing_tax_identifiers = [...props.data.identity.tax_identifiers];
        // console.log('existing_tax_identifiers');
        // console.dir(existing_tax_identifiers);
        if (
          latestTaxIdentifier.value?.is_type &&
          (latestTaxIdentifier.value?.is_type == 'ein' ||
            altTinTypes.value?.includes(latestTaxIdentifier.value?.is_type))
        ) {
          tinType.value = latestTaxIdentifier.value?.is_type;
          console.log('calling checkData from onShowW8 func #1-2');
          checkData();
          return;
        } else if (existing_tax_identifiers?.length > 0) {
          existing_tax_identifiers.sort(function (a, b) {
            return new Date(b.date) - new Date(a.date);
          });
          let existingMatch = existing_tax_identifiers.some(
            (tax_identifier) => {
              if (
                altTinTypes.value?.includes(tax_identifier?.is_type) ||
                tax_identifier?.is_type == 'ein'
              ) {
                tinType.value = tax_identifier.is_type;
                console.log('calling checkData from onShowW8 func #2');
                checkData();
                return;
              }
            }
          );
          if (
            !existingMatch &&
            selectedTaxNumberOption?.value?.label == 'SSN'
          ) {
            tinType.value = TaxNumberTypes.SSN;
            console.log('calling checkData from onShowW8 func #3');
            checkData();
            return;
          }
        } else {
          if (hasCanadianAddress?.value == true) {
            // tinType.value = TaxNumberTypes.FTIN;
            tinType.value = TaxNumberTypes.BN;
          } else if (preferredCountry.value === 'mx') {
            tinType.value = TaxNumberTypes.RFC;
          } else {
            tinType.value = TaxNumberTypes.FTIN;
          }
        }
      }
    };

    const onShowW9 = () => {
      show_w8.value = false;
      hasTaxIdentification.value = false;

      if (altTinTypes.value?.includes(tinType.value)) {
        tinType.value = 'ein';
      } else {
        if (tinType.value !== 'ssn') {
          tinType.value = 'ein';
        }
      }
      console.log('calling checkData from onShowW9 func');
      checkData();
    };

    const hasClickedW9 = ref(false);

    const onShowW9ByClick = () => {
      hasClickedW9.value = true;
      onShowW9();
    };

    const onSelectTaxNumberType = (option) => {
      // console.log('onSelectTaxNumberType');
      tinType.value = option.id;
      console.log('calling checkData from onSelectTaxNumberType func');
      checkData();
    };

    // const handleTaxFormSubmit = async function() {
    //   taxFormSubmitButtonLoading.value = true;
    //   let res = await store.dispatch('taxQuestions/submitTaxForm');
    //   toastResponseInSetup(toast, res, 'Tax form successfully submitted');
    //   if ([200, 201].includes(res?.status)) {
    //     store.commit('updateStepFirstTime', { step: 'Contract', first_time: false });
    //   }
    //   taxFormSubmitButtonLoading.value = false;
    // };

    const signerNameTaxQuestionIds =
      store.state.taxQuestions?.signer_name_tax_question_ids;

    const getTaxFormAnswerByQuestionId = (question) => {
      if (latestTaxFormAnswers.value.length === 0) {
        return null;
      }
      if (question && question?.is_type !== 'group') {
        let a = latestTaxFormAnswers.value.find(
          (answer) => answer.tax_question_id === question.id
        );
        if (
          ['radio', 'select', 'checkbox'].includes(question?.is_type) &&
          a?.answer_options?.length > 0
        ) {
          let option_values = a?.answer_options?.map((ao) => ao?.option?.value);
          return option_values.join(', ');
        } else if (a?.final_value_as_string) {
          if (question?.is_type == 'input_date') {
            return dayjs(a?.final_value_as_string).format('MM-DD-YYYY');
          } else {
            if (signerNameTaxQuestionIds.includes(question?.id)) {
              if (
                store.state.users.currentUser?.first_name &&
                store.state.users.currentUser?.last_name &&
                `${store.state.users.currentUser?.first_name} ${store.state.users.currentUser?.last_name}` !==
                  a?.final_value_as_string
              ) {
                return `${store.state.users.currentUser?.first_name} ${store.state.users.currentUser?.last_name}`;
              }
            }
            return a?.final_value_as_string;
          }
        }
      } else if (question && question?.is_type == 'group') {
        let a_group = latestTaxFormAnswers.value.filter(
          (answer) => answer.tax_question.group_parent_id == question.id
        );
        if (a_group?.length > 0) {
          a_group = a_group.sort(
            (a, b) => a.tax_question.group_order - b.tax_question.group_order
          );
          return a_group.map((a) => a.final_value_as_string).join(', ');
        }
      }
      return null;
    };

    const getTaxFormSubjectByQuestionId = (question) => {
      if (latestTaxFormAnswers.value.length === 0) {
        return null;
      }
      if (question && question?.show_subject == true) {
        let a = latestTaxFormAnswers.value.find(
          (answer) => answer.tax_question_id === question.id
        );
        if (a?.subject) {
          return question?.subject;
        }
      }
    };

    const getTaxFormAnswerObjectByQuestionId = (question) => {
      // console.log('latestTaxFormAnswers.value');
      // console.dir(latestTaxFormAnswers?.value);
      // console.dir(latestTaxFormAnswers);
      // console.log('getTaxFormAnswerObjectByQuestionId - question');
      // console.dir(question);
      if (
        !latestTaxFormAnswers?.value ||
        latestTaxFormAnswers?.value?.length < 1
      ) {
        return null;
      }
      if (
        question &&
        question?.id > 0 &&
        latestTaxFormAnswers?.value?.length > 1
      ) {
        let a = latestTaxFormAnswers.value.find(
          (answer) => answer?.tax_question_id === question?.id
        );
        if (a) {
          return a;
        }
      }
      return false;
    };

    watch(listTaxIndentifiers, (currentValue) => {
      if (currentValue && props.stepName?.includes('Contract')) {
        setUpForm();
      }
    });

    watch(
      () => props.stepName,
      (currentValue) => {
        if (currentValue === 'Contract-1' && latestTaxIdentifier.value) {
          setUpForm();
        }
      }
    );

    // watch(latestTaxIdentifier, (currentValue) => {
    //   if (show_w8.value == true && currentValue?.is_type !== tinType.value) {
    //     onShowW8();
    //   }
    // });

    watch(taxNumber, (value) => {
      console.log('updated taxNumber: ', value);
      console.log('calling checkData from taxNumber watcher');
      checkData();
    });

    watch(tinType, (value) => {
      console.log('updated tinType: ', value);
    });

    watch(
      latestTaxFormType,
      (currentValue) => {
        console.log('updated latestTaxFormType: ', currentValue);
        console.log(
          'calling updateIsButtonDisabled from latestTaxFormType watcher'
        );
        updateIsButtonDisabled();
        if (currentValue && currentValue !== 'w_9') {
          w8Type.value = currentValue;
          if (show_w8.value == false) {
            onShowW8();
          }
        } else if (
          (hasCanadianAddress?.value == true ||
            (preferredCountry.value && preferredCountry.value != 'us')) &&
          hasClickedW9.value == false
        ) {
          // Force W-8 if Carrier has Canadian physical or mailing address
          if (
            allowedW8IsTypes.value &&
            allowedW8IsTypes.value?.length > 0 &&
            !allowedW8IsTypes.value?.includes(defaultW8OptionIsType?.value)
          ) {
            w8Type.value = allowedW8IsTypes.value?.[0];
          } else {
            w8Type.value = defaultW8OptionIsType?.value;
          }
          console.log('setCurrentTaxForm from latestTaxFormType watcher-1');
          setCurrentTaxFormAndSubmission();
          onShowW8();
        } else {
          onShowW9();
        }

        if (store.state.taxQuestions?.tax_questions?.[currentValue] == null) {
          store.dispatch('taxQuestions/getTaxQuestions', currentValue);
        }

        if (
          store.state.taxQuestions?.latest_company_tax_form_submissions?.[
            currentValue
          ] == null
        ) {
          store.dispatch(
            'taxQuestions/getLatestCompanyTaxFormSubmissionByTaxFormIsType',
            currentValue
          );
        }

        if (w8Type.value == currentValue) {
          console.log('setCurrentTaxForm from latestTaxFormType watcher-2');
          setCurrentTaxFormAndSubmission();
        }
      },
      { immediate: true }
    );

    watch(
      latestCompanyTaxFormSubmission,
      (currentValue) => {
        console.log('latestCompanyTaxFormSubmission watcher in Contract-1');
        if (
          currentValue?.is_active == true &&
          (!(allowedW8IsTypes.value?.length > 0) ||
            allowedW8IsTypes.value?.includes(currentValue?.tax_form?.is_type))
        ) {
          store.commit('taxQuestions/setCurrentTaxForm', {
            is_type: currentValue?.tax_form?.is_type,
            id: currentValue?.tax_form?.id,
          });
          store.commit('taxQuestions/setTaxFormSubmission', {
            prop: 'tax_form_id',
            data: currentValue?.tax_form?.id,
          });
        }
      },
      { immediate: true }
    );

    watch(
      submitW8Anyway,
      (newVal) => {
        if (
          newVal &&
          ['bypass_w_9', 'bypass_w_8'].includes(
            latestCompanyTaxFormSubmission.value?.tax_form?.is_type
          )
        ) {
          let latest_w_8_for_company;
          let current_w_8s_for_company = [];
          if (
            store.taxQuestions?.latest_company_tax_form_submissions?.w_8ben_e
              ?.id > 0
          ) {
            current_w_8s_for_company.push({
              type: 'w_8bene_e',
              updated_at:
                store.taxQuestions?.latest_company_tax_form_submissions
                  ?.w_8ben_e?.updated_at,
            });
          } else if (
            store.taxQuestions?.latest_company_tax_form_submissions?.w_8ben
              ?.id > 0
          ) {
            current_w_8s_for_company.push({
              type: 'w_8ben',
              updated_at:
                store.taxQuestions?.latest_company_tax_form_submissions?.w_8ben
                  ?.updated_at,
            });
          } else if (
            store.taxQuestions?.latest_company_tax_form_submissions
              ?.w_8ben_e_substitute?.id > 0
          ) {
            current_w_8s_for_company.push({
              type: 'w_8bene_e_substitute',
              updated_at:
                store.taxQuestions?.latest_company_tax_form_submissions
                  ?.w_8ben_e_substitute?.updated_at,
            });
          } else if (
            store.taxQuestions?.latest_company_tax_form_submissions?.w_8eci
              ?.id > 0
          ) {
            current_w_8s_for_company.push({
              type: 'w_8eci',
              updated_at:
                store.taxQuestions?.latest_company_tax_form_submissions?.w_8eci
                  ?.updated_at,
            });
          } else if (
            store.taxQuestions?.latest_company_tax_form_submissions?.form_8233
              ?.id > 0
          ) {
            current_w_8s_for_company.push({
              type: 'form_8233',
              updated_at:
                store.taxQuestions?.latest_company_tax_form_submissions
                  ?.form_8233?.updated_at,
            });
          }

          if (current_w_8s_for_company?.length > 0) {
            latest_w_8_for_company = current_w_8s_for_company.reduce(
              (prev, current) => {
                return prev.updated_at > current.updated_at ? prev : current;
              }
            );
            latest_w_8_for_company =
              store.taxQuestions?.latest_company_tax_form_submissions?.[
                latest_w_8_for_company?.type
              ];
            if (latest_w_8_for_company?.id > 0) {
              store.commit('taxQuestions/setCurrentTaxForm', {
                is_type: latest_w_8_for_company?.tax_form?.is_type,
                id: latest_w_8_for_company?.tax_form?.id,
              });
            }
          } else {
            store.commit('taxQuestions/setCurrentTaxForm', {
              is_type: 'w_8ben',
              id: 2,
            });
          }
        }
      },
      { immediate: true }
    );

    watch(
      submitW9Anyway,
      (newVal) => {
        if (
          newVal &&
          ['bypass_w_9', 'bypass_w_8'].includes(
            latestCompanyTaxFormSubmission.value?.tax_form?.is_type
          )
        ) {
          store.commit('taxQuestions/setCurrentTaxForm', {
            is_type: 'w_9',
            id: 1,
          });
        }
      },
      { immediate: true }
    );

    onBeforeMount(() => {
      store.dispatch('taxQuestions/getLatestCompanyTaxFormSubmission');
    });

    onMounted(() => {
      if (
        altTinTypes.value?.includes(latestTaxIdentifier?.value?.is_type) ||
        altTinTypes.value?.includes(defaultTinOption?.value?.toLowerCase())
      ) {
        onShowW8();
      }

      store.dispatch('taxQuestions/getTaxQuestions', 'w_9');
      console.log('calling updateIsButtonDisabled from onMounted func');
      updateIsButtonDisabled();
    });

    watch(
      show_w8,
      (currentValue) => {
        console.log('calling updateIsButtonDisabled from show_w8 watcher');
        updateIsButtonDisabled();
        if (currentValue == true) {
          if (store.state.taxQuestions?.tax_questions?.w_8ben_e == null) {
            store.dispatch('taxQuestions/getTaxQuestions', 'w_8ben_e');
          }
          if (
            store.state.taxQuestions?.latest_company_tax_form_submissions
              ?.w_8ben_e == null &&
            hasCheckedForTaxFormSubmission.value?.['w_8ben_e'] == false
          ) {
            store.dispatch(
              'taxQuestions/getLatestCompanyTaxFormSubmissionByTaxFormIsType',
              'w_8ben_e'
            );
          }
          if (
            store.state.taxQuestions?.tax_questions?.w_8ben_e_substitute == null
          ) {
            store.dispatch(
              'taxQuestions/getTaxQuestions',
              'w_8ben_e_substitute'
            );
          }
          if (
            store.state.taxQuestions?.latest_company_tax_form_submissions
              ?.w_8ben_e_substitute == null &&
            hasCheckedForTaxFormSubmission.value?.['w_8ben_e_substitute'] ==
              false
          ) {
            store.dispatch(
              'taxQuestions/getLatestCompanyTaxFormSubmissionByTaxFormIsType',
              'w_8ben_e_substitute'
            );
          }
          if (store.state.taxQuestions?.tax_questions?.w_8ben == null) {
            store.dispatch('taxQuestions/getTaxQuestions', 'w_8ben');
          }
          if (
            store.state.taxQuestions?.latest_company_tax_form_submissions
              ?.w_8ben == null &&
            hasCheckedForTaxFormSubmission.value?.['w_8ben'] == false
          ) {
            store.dispatch(
              'taxQuestions/getLatestCompanyTaxFormSubmissionByTaxFormIsType',
              'w_8ben'
            );
          }
          if (store.state.taxQuestions?.tax_questions?.w_8eci == null) {
            store.dispatch('taxQuestions/getTaxQuestions', 'w_8eci');
          }
          if (
            store.state.taxQuestions?.latest_company_tax_form_submissions
              ?.w_8eci == null &&
            hasCheckedForTaxFormSubmission.value?.['w_8eci'] == false
          ) {
            store.dispatch(
              'taxQuestions/getLatestCompanyTaxFormSubmissionByTaxFormIsType',
              'w_8eci'
            );
          }
          if (store.state.taxQuestions?.tax_questions?.form_8233 == null) {
            store.dispatch('taxQuestions/getTaxQuestions', 'form_8233');
          }
          if (
            store.state.taxQuestions?.latest_company_tax_form_submissions
              ?.form_8233 == null &&
            hasCheckedForTaxFormSubmission.value?.['form_8233'] == false
          ) {
            store.dispatch(
              'taxQuestions/getLatestCompanyTaxFormSubmissionByTaxFormIsType',
              'form_8233'
            );
          }
        } else {
          store.commit('taxQuestions/setCurrentTaxForm', {
            is_type: 'w_9',
            id: 1,
          });
        }
      },
      { immediate: true }
    );

    watch(firstTimeVal, (currentValue) => {
      if (currentValue == true && !submitW9Anyway.value) {
        if (
          altTinTypes.value?.includes(latestTaxIdentifier.value?.is_type) ||
          altTinTypes.value?.includes(defaultTinOption?.value)
        ) {
          onShowW8();
        }
      }
    });

    watch(hasCanadianAddress, (currentValue) => {
      if (currentValue == true || currentValue == false) {
        store.commit('taxQuestions/setHasCanadianAddress', currentValue);
      }
    });

    const emitExitModal = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'emitExitModal' called"
      );
      emit('exit-modal');
    };

    const toggleRequiredCount = computed(() => {
      return store.state.taxQuestions?.tax_question_toggle_required_count;
    });

    watch(toggleRequiredCount, (newVal, oldVal) => {
      if (newVal > oldVal) {
        console.log(
          'calling updateIsButtonDisabled from toggleRequiredCount watcher'
        );
        updateIsButtonDisabled();
      }
    });

    const handleBypassW9 = async () => {
      let res = await store.dispatch('taxQuestions/submitBypassW9TaxForm');
      if ([200, 201].includes(res?.status)) {
        store.commit('firstTime/updateStepFirstTime', {
          step: 'Contract',
          first_time: false,
        });
        handleNext();
      }
    };
    const handleCompleteW9Anyway = () => {
      submitW9Anyway.value = true;
    };
    const handleViewW8 = () => {
      onShowW8();
    };
    const handleBypassW8 = async () => {
      let res = await store.dispatch('taxQuestions/submitBypassW8TaxForm');
      if ([200, 201].includes(res?.status)) {
        store.commit('firstTime/updateStepFirstTime', {
          step: 'Contract',
          first_time: false,
        });
        handleNext();
      }
    };
    const handleCompleteW8Anyway = () => {
      submitW8Anyway.value = true;
    };
    const handleViewW9 = () => {
      // onShowW9();
      onShowW9ByClick();
    };

    return {
      componentIdentifier,
      handleNext,
      handleBack,
      handleEdit,
      handleW8Submit,
      buttonsToDisplay,
      isButtonDisabled,
      isNavigationDisabled,
      firstTime,
      show_w8,
      handleSelectRadio,
      handleDropdownSelectTinType,
      handleDropdownSelect,
      tinType,
      // ein,
      // ssn,
      onShowW8,
      taxNumber,
      hasTaxIdentification,
      isTaxIdentificationSaved,
      onShowW9,
      onSelectTaxNumberType,
      taxNumberTypeOptions,
      selectedTaxNumberOption,
      tinValueWithoutFormat,
      defaultTinOption,
      defaultW8Option,
      w8Type,
      handleDropdownSelectW8Type,
      w8TaxFormText,
      tax_number,
      taxFormIsTypeToArray,
      // handleTaxFormSubmit,
      latestTaxFormType,
      currentTaxFormPreviewUrl,
      latestTaxFormAnswers,
      getTaxFormAnswerByQuestionId,
      getTaxFormAnswerObjectByQuestionId,
      latestTaxIdentifier,
      einSsnToggleButton,
      latestTaxSubmissionTaxQuestionSections,
      taxFormSubmitButtonLoading,
      physicalAddress,
      mailingAddress,
      hasCanadianAddress,
      latestCompanyTaxFormSubmission,
      latestCompanyTaxFormSubmissionTaxFormIsType,
      getTaxFormSubjectByQuestionId,
      emitExitModal,
      validateIdentifier,
      finalizedTaxIdentifierIsType,
      onShowW9ByClick,
      isW9Optional,
      isW8Optional,
      submitW9Anyway,
      submitW8Anyway,
      handleBypassW9,
      handleCompleteW9Anyway,
      handleViewW8,
      handleBypassW8,
      handleCompleteW8Anyway,
      handleViewW9,
    };
  },
  data() {
    return {
      items: [],
      selectOptions: ['C', 'S', 'P'],
      tinOption: ['EIN', 'SSN'],
      altTins: ['bn', 'rfc', 'giin', 'ftin', 'sin'],
      w8Option: ['W-8BEN', 'W-8BEN-E', 'W-8ECI', 'Form 8233'],
      w8OptionsLoaded: false,
      hidden: [],
      init_hidden: false,
      editTaxForm: false,
      dynamicComponentByType: {
        html: processedHtmlRef,
        div: undefined,
      },
      checkmark:
        '<svg style="position:relative;top:-1px;" width="20" height="20" viewBox="0 0 36 37" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M18 34.25C26.6985 34.25 33.75 27.1985 33.75 18.5C33.75 15.985 33.1605 13.6076 32.1121 11.4986L18.5493 25.0614L16.9583 26.6524L15.3673 25.0614L8.43733 18.1315L11.6193 14.9495L16.9583 20.2885L29.5041 7.74266C26.6299 4.67022 22.5392 2.75 18 2.75C9.30152 2.75 2.25 9.80152 2.25 18.5C2.25 27.1985 9.30152 34.25 18 34.25Z" fill="#C7CBDB"/></svg>',
      required: {
        w_8ben: {},
        w_8ben_e: {},
        w_8ben_e_substitute: {},
        w_8eci: {},
        form_8233: {},
      },
      validationMessage: {
        ein: 'EIN must be exactly 9 digits',
        ssn: 'SSN must be exactly 9 digits',
        rfc: 'RFC must be either 12 or 13 characters',
        giin: 'Please enter your full GIIN',
        ftin: 'Please enter your full FTIN',
        sin: 'SIN must be exactly 9 digits',
        bn: 'BN must be exactly 9 digits',
      },
    };
  },
  computed: {
    brokerName() {
      return this.$store.state.carriers?.carrierData?.connection?.issuer
        ?.legal_name;
    },
    requiredCompleted() {
      let has_tax_identifier =
        this.data?.identity?.tax_identifiers?.length > 0 ||
        this.data?.contracts_information?.tin_value?.length > 0 ||
        this.selected_tax_number?.length > 0;
      console.log('this.required', this.required);
      console.log(
        'this.required?.[this.currentTaxForm]',
        this.required?.[this.currentTaxForm]
      );
      console.log('this.required', this.required);
      console.log('this.currentTaxForm', this.currentTaxForm);
      if (['bypass_w_9', 'bypass_w_8'].includes(this.currentTaxForm)) {
        return true;
      } else if (this.currentTaxForm == 'w_9') {
        let tax_classification =
          this.data?.identity?.tax_classification ||
          this.currentTaxClassification ||
          this.selected_federal_tax_class;
        if (
          this.selected_federal_tax_class == 'limited_liability_company' &&
          !this.selected_llc_sub_classification
        ) {
          return false;
        }
        let has_tax_classification = tax_classification?.length > 0;
        return has_tax_classification && has_tax_identifier;
      } else if (
        Object.values(this.required?.[this.currentTaxForm])?.length > 0
      ) {
        return this.stepName === 'Contract-1'
          ? !Object.values(this.required[this.currentTaxForm]).some(
              (el) => !el
            ) && has_tax_identifier
          : false;
      }
      return true;
    },
    paymentType() {
      return this.$store.state.payments.payment_type;
    },
    selected: function (props) {
      if (
        props.data?.federal_tax_class?.includes('limited_liability_company') ||
        ['llc_c_corporation', 'llc_s_corporation', 'llc_partnership'].includes(
          this.selected_federal_tax_class
        )
      ) {
        return true;
      } else {
        const updatedValue = {
          ...props.data.contracts_information,
          tax_classification: '',
        };
        props.updateField('contracts_information', updatedValue);
        return false;
      }
    },
    llcType: function (props) {
      const federalTaxObj = stepInformation.contract.federal_tax_class.find(
        (el) => el.value === props.data?.federal_tax_class?.toString()
      );
      if (federalTaxObj) {
        return federalTaxObj.name;
      } else if (
        ['llc_c_corporation', 'llc_s_corporation', 'llc_partnership'].includes(
          props.data?.federal_tax_class?.toString()
        )
      ) {
        switch (props.data?.federal_tax_class?.toString()) {
          case 'llc_c_corporation':
            return 'Limited Liability Company (C Corporation)';
          case 'llc_s_corporation':
            return 'Limited Liability Company (S Corporation)';
          case 'llc_partnership':
            return 'Limited Liability Company (Partnership)';
          default:
            return '';
        }
      }
      return '';
    },
    currentTaxClassification() {
      return this.$store.state.onboardingPacket?.onboardingPacketBody?.carrier
        ?.tax_classification;
    },
    taxClassification: function (props) {
      const taxClass = props.data.contracts_information.tax_classification;
      switch (taxClass) {
        case 'C':
          return 'Corporation';
        case 'P':
          return 'Partnership';
        case 'S':
          return 'S Corporation';
        default:
          return '';
      }
    },
    isButtonEnabled: function () {
      if (this.data?.federal_tax_class === null) {
        return false;
      }
      if (
        this.data?.federal_tax_class === 'limited_liability_company' &&
        this.data?.contracts_information.tax_classification === ''
      ) {
        return false;
      }

      if (
        this.tinType === 'ein' &&
        (this.data?.contracts_information.tin_value === '' ||
          this.data?.federal_tax_class === '')
      ) {
        return false;
      }

      if (
        this.tinType === 'ssn' &&
        (this.data?.contracts_information.tin_value === '' ||
          this.data?.federal_tax_class === '')
      ) {
        return false;
      }

      if (
        this.tinType === 'rfc' &&
        (this.data?.contracts_information.tin_value === '' ||
          this.data?.contracts_information.tin_value.length < 12 ||
          this.data?.contracts_information.tin_value.length > 13 ||
          this.data?.federal_tax_class === '')
      ) {
        return false;
      }

      if (
        this.tinType === 'giin' &&
        (this.data?.contracts_information.tin_value === '' ||
          this.data?.contracts_information.tin_value.length < 19 ||
          this.data?.federal_tax_class === '')
      ) {
        return false;
      }

      if (
        (this.tinType === 'sin' || this.tinType === 'bn') &&
        (this.data?.contracts_information.tin_value === '' ||
          this.data?.contracts_information.tin_value.length !== 9 ||
          this.data?.federal_tax_class === '')
      ) {
        return false;
      }

      return true;
    },
    orderedSection() {
      return _.orderBy(this.data.company_question_sections, 'order');
    },
    federalTaxClass() {
      return this.items?.federal_tax_class || [];
    },
    currentTaxForm() {
      return this.$store.state.taxQuestions?.current_tax_form;
    },
    taxQuestionSections: {
      cache: false,
      get() {
        return (
          this.$store.state.taxQuestions?.tax_questions?.[
            this.currentTaxForm
          ]?.[0]?.tax_question_sections || []
        );
      },
    },
    currentSection() {
      return this.$store.state.taxQuestions.section;
    },
    tax_form_selection_tooltip() {
      return this.items?.tax_form_selection_tooltip;
    },
    onboardingPacketId() {
      return this.$store.state.onboardingPacket.onboarding_id;
    },
    selected_federal_tax_class() {
      return this.$store.state.taxQuestions?.federal_tax_class;
    },
    selected_tax_number() {
      return this.$store.state.taxQuestions?.tax_number;
    },
    selected_llc_sub_classification() {
      return this.$store.state.taxQuestions?.llc_sub_classification;
    },
  },
  watch: {
    '$store.state.carriers.carrierData': {
      handler: function (val) {
        if (this.w8OptionsLoaded) {
          return;
        }
        let self = this;
        let allowed = val?.connection?.issuer?.allowed_w8_is_types;
        if (allowed?.length > 0) {
          let options = [];
          allowed.forEach((is_type) => {
            let option;
            switch (is_type) {
              case 'w_8ben':
                option = 'W-8BEN';
                break;
              case 'w_8ben_e':
                option = 'W-8BEN-E';
                break;
              case 'w_8ben_e_substitute':
                option = 'W-8BEN-E(S)';
                break;
              case 'w_8eci':
                option = 'W-8ECI';
                break;
              case 'form_8233':
                option = 'Form 8233';
                break;
            }
            if (option) {
              options.push(option);
            }
          });
          if (options.length > 0) {
            self.w8Option = options;
          }
        }
        if (val?.id > 0) {
          self.w8OptionsLoaded = true;
        }
      },
      immediate: true,
    },
    '$store.state.company.companyData': {
      handler: function (val) {
        if (this.w8OptionsLoaded) {
          return;
        }
        let self = this;
        let allowed = val?.allowed_w8_is_types;
        if (allowed?.length > 0) {
          let options = [];
          allowed.forEach((is_type) => {
            let option;
            switch (is_type) {
              case 'w_8ben':
                option = 'W-8BEN';
                break;
              case 'w_8ben_e':
                option = 'W-8BEN-E';
                break;
              case 'w_8ben_e_substitute':
                option = 'W-8BEN-E(S)';
                break;
              case 'w_8eci':
                option = 'W-8ECI';
                break;
              case 'form_8233':
                option = 'Form 8233';
                break;
            }
            if (option) {
              options.push(option);
            }
          });
          if (options.length > 0) {
            self.w8Option = options;
          }
        }
        if (val?.id > 0) {
          self.w8OptionsLoaded = true;
        }
      },
      immediate: true,
    },
    '$store.state.company.companyData.allowed_tins': {
      handler: function (val) {
        if (val?.length > 0) {
          let options = [];
          val.forEach((is_type) => {
            if (['bn', 'rfc', 'giin', 'ftin', 'sin'].includes(is_type)) {
              options.push(is_type);
            }
          });
          if (options.length > 0) {
            this.altTins = options;
          }
        }
      },
      immediate: true,
    },
    taxQuestionSections: {
      handler: async function (val) {
        if (val?.length > 0) {
          // this.$store.commit("toggleDisabledButton", { bool: true, source: 'ContractStepOne #4' });
          let self = this;
          // let required = {};
          // let section_order = [];
          let taxSections = [...self.taxQuestionSections];
          let requiredQuestions = {};
          taxSections.forEach((section) => {
            section.tax_questions.forEach((tax_question) => {
              if (tax_question.is_required) {
                requiredQuestions[tax_question.id] =
                  self.getTaxFormAnswerByQuestionId(tax_question)
                    ? true
                    : false;
                // if (tax_question?.question?.most_recent_previous_answer || tax_question.question?.current_answer) {
                //   if (!tax_question?.question?.is_type?.includes('file')) {
                //     requiredQuestions[tax_question.id] = true;
                //   } else {
                //     let prev = tax_question?.question?.most_recent_previous_answer;
                //     let curr = tax_question?.question?.current_answer;
                //     if (prev?.file_uploads?.length > 0 || curr?.file_uploads?.length > 0) {
                //       requiredQuestions[tax_question.id] = true;
                //     } else {
                //       requiredQuestions[tax_question.id] = false;
                //     }
                //   }
                // } else if (tax_question?.tax_question_conditions?.parents?.length > 0) {
                //   let filteredCQC = tax_question.tax_question_conditions.parents.filter((cqc) => cqc.is_by_evaluation_js == false && cqc.is_required == true);
                //   if (filteredCQC?.length > 0) {
                //     requiredQuestions[tax_question.id] = false;
                //   }
                // } else {
                //   requiredQuestions[tax_question.id] = false;
                // }
              }
              if (tax_question?.child_questions?.length > 0) {
                tax_question.child_questions.forEach((child_question) => {
                  if (child_question?.is_required) {
                    requiredQuestions[child_question.id] =
                      self.getTaxFormAnswerByQuestionId(child_question)
                        ? true
                        : false;
                  }
                });
              }
            });
            // required[section.order] = requiredQuestions;
            // section_order.push(section.order);
          });
          self.required[self.currentTaxForm] = requiredQuestions;
        }
      },
      immediate: true,
    },
    requiredCompleted: {
      deep: true,
      handler: async function (nv) {
        let self = this;
        if (self.stepName == 'Contract-1') {
          if (nv == true) {
            await self.$store.commit('toggleDisabledButton', {
              bool: false,
              source: 'ContractStepOne #5',
            });
          } else {
            await self.$store.commit('toggleDisabledButton', {
              bool: true,
              source: 'ContractStepOne #6',
            });
          }
        }
      },
      immediate: true,
    },
    // isButtonEnabled: function(newVal) {
    //   if (this.stepName == 'Contract-1') {
    //     this.$store.commit("toggleDisabledButton", !newVal);
    //   }
    // },
  },
  mounted() {
    this.items = stepInformation.contract;
    this.$store.dispatch('taxQuestions/getTaxForms');
    this.$store.commit('taxQuestions/setTaxFormSubmission', {
      prop: 'onboarding_packet_id',
      data: this.onboardingPacketId,
    });
    if (!this.selected_federal_tax_class && this.currentTaxClassification) {
      this.$store.commit(
        'taxQuestions/setFederalTaxClass',
        this.currentTaxClassification
      );
    }
    if (this.data?.contracts_information?.tin_value?.length > 0) {
      this.$store.commit(
        'taxQuestions/setTaxNumber',
        this.data?.contracts_information?.tin_value
      );
    }
  },
  methods: {
    checkLLC(dataVal, itemVal) {
      if (
        itemVal === 'limited_liability_company' &&
        ['llc_c_corporation', 'llc_s_corporation', 'llc_partnership'].includes(
          dataVal
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    checkLLCSub(dataVal) {
      switch (dataVal) {
        case 'llc_c_corporation':
          return 'C';
        case 'llc_s_corporation':
          return 'S';
        case 'llc_partnership':
          return 'P';
        default:
          return null;
      }
    },
    checkButtonDisabled() {
      if (
        this.$props.data?.federal_tax_class === 'limited_liability_company' &&
        this.$props.data?.contracts_information.tax_classification === ''
      ) {
        this.$store.commit('toggleDisabledButton', {
          bool: true,
          source: 'ContractStepOne #1',
        });
      } else {
        if (
          this.$props.data?.contracts_information?.tin_type &&
          this.$props.data?.contracts_information?.tin_value
        ) {
          this.$store.commit('toggleDisabledButton', {
            bool: false,
            source: 'ContractStepOne #2',
          });
        } else {
          this.$store.commit('toggleDisabledButton', {
            bool: true,
            source: 'ContractStepOne #3',
          });
        }
      }
    },
    order(array, orderBy) {
      return _.orderBy(array, orderBy);
    },
    toggleRequired(question_id, state) {
      console.log(
        'toggleRequired...question_id: ' + question_id + ', state: ' + state
      );
      this.$store.commit(
        'taxQuestions/setIncrementTaxQuestionToggleRequiredCount'
      );
      if (
        this.required?.[this.currentTaxForm]?.[question_id] === true ||
        this.required?.[this.currentTaxForm]?.[question_id] === false
      ) {
        this.required[this.currentTaxForm][question_id] = state;
        console.log('updated required...');
      }
    },
    initHidden() {
      let self = this;
      let taxSections = [...self.taxQuestionSections];
      taxSections.forEach((el) => {
        el.tax_questions.forEach((tax_question) => {
          if (tax_question?.tax_question_conditions?.parents?.length > 0) {
            self.hidden.push(tax_question?.id);
          }
        });
      });
    },
    hideHidden(question_id) {
      let index = this.hidden.indexOf(question_id);
      if (index == -1) {
        this.hidden.push(question_id);
      }
    },
    unhideHidden(question_id) {
      let index = this.hidden.indexOf(question_id);
      if (index > -1) {
        this.hidden.splice(index, 1);
      }
    },
    processedHtml(question) {
      // let returnedSubject = question?.subject;
      // if (question?.child_questions?.length > 0) {
      //   question.child_questions.forEach((child, index) => {
      //     let idx = index + 1;
      //     let val = this.getTaxFormAnswerByQuestionId(child);
      //     if (child?.is_type == 'input_date') {
      //       val = dayjs(val).format('MM-DD-YYYY');
      //     }
      //       returnedSubject = returnedSubject.replace(
      //         '${child_'+idx+'}',
      //         '<div class="inline-flex px-3 mr-0.5 border-b border-dark-gray h-4 relative justify-center bg-gray-10" style="min-width: 77px;"><span class="text-body-color relative bottom-0">'+ val +'</span></div>'
      //       );
      //   });
      // }
      // return {
      //     template: '<div>' + returnedSubject + '</div>'
      // }

      let self = this;
      let returnedSubject = question?.subject;
      returnedSubject = returnedSubject.replace(
        '<div class="tax-question-html"><p>$',
        '<div class="tax-question-html relative -top-1.5"><p>$'
      );
      returnedSubject = returnedSubject.replace(
        '<div class="tax-question-html"> <p>$',
        '<div class="tax-question-html relative -top-1.5"> <p>$'
      );
      returnedSubject = returnedSubject.replace(
        '<div class="tax-question-html" style="margin-top: 4.5px;">',
        '<div class="tax-question-html">'
      );

      if (question?.child_questions?.length > 0) {
        question.child_questions.forEach((child, index) => {
          let idx = index + 1;
          if (child?.is_type == 'radio') {
            let optionCount = child?.options?.length;
            let val = this.getTaxFormAnswerByQuestionId(child);
            if (optionCount > 0) {
              for (let i = 1; i <= optionCount; i++) {
                let option =
                  child?.options?.length > 0
                    ? child.options.filter((o) => o?.order == i)
                    : [];
                let newVal = option?.[0]?.value == val ? self?.checkmark : '';
                // let borderColor = 'border-yellow-500';
                // if (newVal == '') {
                //   borderColor = 'border-placeholder-gray';
                // }
                let borderColor = 'border-placeholder-gray';
                returnedSubject = returnedSubject.replace(
                  '${child_' + idx + '_' + i + '}',
                  '<div class="inline-flex mr-0.5 border ' +
                    borderColor +
                    ' rounded-full h-5 w-5 relative justify-center cursor-not-allowed"><span class="text-body-color relative bottom-0">' +
                    (newVal === null ? '' : newVal) +
                    '</span></div>'
                );
              }
            }
          } else if (child?.is_type == 'checkbox') {
            let optionCount = child?.options?.length;
            let val = this.getTaxFormAnswerByQuestionId(child);
            if (optionCount > 1) {
              for (let i = 1; i <= optionCount; i++) {
                let option =
                  child?.options?.length > 0
                    ? child.options.filter((o) => o?.order == i)
                    : [];
                let newVal = option?.[0]?.value == val ? self?.checkmark : '';
                // let borderColor = 'border-yellow-500';
                // if (newVal == '') {
                //   borderColor = 'border-placeholder-gray';
                // }
                let borderColor = 'border-placeholder-gray';
                returnedSubject = returnedSubject.replace(
                  '${child_' + idx + '_' + i + '}',
                  '<div class="inline-flex mr-0.5 border ' +
                    borderColor +
                    ' rounded-full h-5 w-5 relative justify-center cursor-not-allowed"><span class="text-body-color relative bottom-0">' +
                    (newVal === null ? '' : newVal) +
                    '</span></div>'
                );
              }
            } else if (optionCount == 1) {
              for (let i = 1; i <= optionCount; i++) {
                let option =
                  child?.options?.length > 0
                    ? child.options.filter((o) => o?.order == i)
                    : [];
                let newVal = option?.[0]?.value == val ? self?.checkmark : '';
                // let borderColor = 'border-yellow-500';
                // if (newVal == '') {
                //   borderColor = 'border-placeholder-gray';
                // }
                let borderColor = 'border-placeholder-gray';
                returnedSubject = returnedSubject.replace(
                  '${child_' + idx + '}',
                  '<div class="inline-flex mr-0.5 border ' +
                    borderColor +
                    ' rounded-full h-5 w-5 relative justify-center cursor-not-allowed"><span class="text-body-color relative bottom-0">' +
                    (newVal === null ? '' : newVal) +
                    '</span></div>'
                );
              }
            }
          } else {
            let val = this.getTaxFormAnswerByQuestionId(child);
            if (val?.length > 5 && child?.is_type == 'input_date') {
              val = dayjs(val).format('MM-DD-YYYY');
            }
            returnedSubject = returnedSubject?.replaceAll(
              '${child_' + idx + '}',
              '<div class="inline-flex px-3 mr-0.5 border-b border-dark-gray h-4 relative justify-center cursor-not-allowed" style="min-width: 77px;"><span class="text-body-color relative bottom-0">' +
                (val === null ? '' : val) +
                '</span></div>'
            );
          }
        });
      }
      returnedSubject = returnedSubject?.replaceAll('%3B', ';');
      // let comp = {
      //     template: '<div>' + returnedSubject + '</div>',
      //     is: null
      // }
      // return shallowRef(comp);
      return returnedSubject;
    },
  },
};
</script>

<style scoped>
p {
  @apply text-left;
}
.categoryParagraph {
  @apply mb-1 text-xxs font-bold uppercase text-dark-blue;
}

.input-padding {
  padding: 15px 24px;
}

.small-font {
  line-height: 24px;
}

/* .vcp.vcp--expanded > .vcp__body > .vcp__body-content {
  border-top: 1px solid #C7CBDB;
}
.vcp__header {
  background-color: "#EDEDF0";
} */
</style>
